// src/pages/NotitiesTaken.js
// vNT-009

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

// ---------------- helpers ----------------
function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[.,;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function firstWord(s) {
  const t = norm(s);
  return t.split(" ").filter(Boolean)[0] || "";
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function pickOrgName(o) {
  return (
    o?.name ||
    o?.naam ||
    o?.bedrijfsnaam ||
    o?.organisation_name ||
    o?.organisatie_naam ||
    o?.organisatie ||
    ""
  );
}

function pickOrgSearchName(o) {
  return (
    o?.search_name ||
    o?.zoeknaam ||
    o?.organisation_search_name ||
    o?.organisatie_zoeknaam ||
    pickOrgName(o)
  );
}

function isPrivateRelation(o) {
  const entityType = String(
    o?.relation_entity_type ||
    o?.relation_type ||
    o?.entity_type ||
    ""
  )
    .toLowerCase()
    .trim();

  return (
    entityType === "private" ||
    entityType === "particulier" ||
    entityType === "person" ||
    entityType === "persoon"
  );
}

function pickContactName(c) {
  const searchName = String(c?.search_name || c?.zoeknaam || "").trim();
  if (searchName) return searchName;

  const fn = String(c?.first_name || c?.voornaam || "").trim();
  const ln = String(c?.last_name || c?.achternaam || "").trim();
  const full = String(c?.full_name || c?.naam || "").trim();

  if (ln && fn) return `${ln}, ${fn}`;
  if (ln) return ln;
  if (fn) return fn;

  return full || "";
}

function pickPersonnelName(p) {
  const searchName = String(p?.search_name || p?.zoeknaam || "").trim();
  if (searchName) return searchName;

  const fn = String(p?.first_name || p?.voornaam || "").trim();
  const ln = String(p?.last_name || p?.achternaam || "").trim();
  const prefix = String(p?.name_prefix || p?.tussenvoegsel || "").trim();
  const full = String(p?.full_name || p?.name || p?.naam || "").trim();

  const last = [prefix, ln].filter(Boolean).join(" ").trim();

  if (last && fn) return `${last}, ${fn}`;
  if (last) return last;
  if (fn) return fn;

  return full || "";
}

function pickItemTitle(it) {
  return it?.title || it?.titel || it?.subject || "";
}
function pickItemBody(it) {
  return it?.body || it?.tekst || it?.text || "";
}
function pickItemOrgName(it) {
  return it?.organisation_name || it?.organisatie_naam || it?.organisatie || "";
}
function pickItemContactName(it) {
  return it?.contact_name || it?.contactpersoon || it?.contactpersoon_naam || "";
}

function pickItemPersonnelName(it) {
  return it?.personnel_name || it?.personeel_naam || it?.personeel || "";
}

function pickItemPersonnelPhone(it) {
  return it?.personnel_phone || it?.personeel_telefoon || "";
}

function pickItemPersonnelMobile(it) {
  return it?.personnel_mobile || it?.personeel_mobiel || "";
}

function pickItemPersonnelEmail(it) {
  return it?.personnel_email || it?.personeel_email || "";
}

function pickItemCreatedByName(it) {
  return it?.created_by_user_name || it?.created_by_name || it?.aangemaakt_door || "";
}

function pickItemPhone(it) {
  return it?.organisation_phone || it?.phone || it?.telefoon || "";
}

function pickItemMobile(it) {
  return it?.organisation_mobile || it?.mobile || it?.mobiel || "";
}

function pickItemEmail(it) {
  return it?.organisation_email || it?.email || "";
}

function pickCompanyCodeName(row) {
  return (
    row?.company_code_name ||
    row?.bedrijfscode_naam ||
    row?.bedrijfscode ||
    row?.company_code ||
    ""
  );
}

function selectedCompanyCodeIdsFromScope() {
  const raw =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  return String(raw || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function selectedCompanyCodesForForm(companyCodes = []) {
  const activeCodes = (companyCodes || []).filter((cc) => cc?.active !== false);
  const selectedIds = selectedCompanyCodeIdsFromScope();

  if (!selectedIds.length) return activeCodes;

  const selectedSet = new Set(selectedIds.map(String));
  const filtered = activeCodes.filter((cc) => selectedSet.has(String(cc.id)));

  return filtered.length ? filtered : activeCodes;
}

function firstCompanyCodeIdFromScope(companyCodes = []) {
  const selected = selectedCompanyCodesForForm(companyCodes);
  const first = selected[0];
  return first?.id ? String(first.id) : "";
}

function findCompanyCodeName(companyCodes = [], id = "") {
  const hit = (companyCodes || []).find((cc) => String(cc.id) === String(id || ""));
  return hit?.name || hit?.code || "";
}

function pickItemStatus(it) {
  return it?.status || "";
}
function pickItemDueISO(it) {
  const v = it?.due_date || it?.vervaldatum || "";
  return String(v || "").slice(0, 10);
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let parsedUser = {};
  try {
    parsedUser = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  const role = parsedUser?.role || "user";

  const companyCodeIds =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  let lastErr;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": companyCodeIds || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), { ...options, headers });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}${msg ? ` - ${msg}` : ""}`);
      }
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) return await res.json();
      return await res.text();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Failed to fetch");
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) {
        cleanup(null);
      }
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function handleEmailGridClick(e, email) {
  stopGridClick(e);

  const mail = String(email || "").trim();

  if (!mail || mail === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(mail)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();

  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();

  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");

  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);

  const c = String(choice || "").trim().toLowerCase();

  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (text === null) return;

    const message = String(text || "").trim();

    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: raw,
          text: message,
        }),
      });

      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }

    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = String(settings.default_whatsapp_text || "").trim();
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

function capFirst(s) {
  const v = String(s || "").trim();
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function replacePuntToken(s) {
  const v = String(s || "");
  return v
    .replace(/\bpunt\b/gi, ".")
    .replace(/\bpuntje\b/gi, ".")
    .replace(/\s+\./g, ".");
}

// ---- date speech parsing (NL months + digits) ----
const MONTHS_NL = {
  januari: 1, jan: 1,
  februari: 2, feb: 2,
  maart: 3, mrt: 3,
  april: 4, apr: 4,
  mei: 5,
  juni: 6, jun: 6,
  juli: 7, jul: 7,
  augustus: 8, aug: 8,
  september: 9, sep: 9,
  okt: 10, oktober: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

function parseDateFromSpeechToISO(raw) {
  const t0 = String(raw || "").trim();
  if (!t0) return "";

  const digits = t0.replace(/\D/g, "");
  if (digits.length === 8) {
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    const iso = `${yyyy}-${mm}-${dd}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  }

  const m1 = t0.match(/(\d{1,2})\D+(\d{1,2})\D+(\d{4})/);
  if (m1) {
    const dd = String(m1[1]).padStart(2, "0");
    const mm = String(m1[2]).padStart(2, "0");
    const yyyy = String(m1[3]);
    return `${yyyy}-${mm}-${dd}`;
  }

  const t = norm(t0);
  const m2 = t.match(/^(\d{1,2})\s+([a-z]+)\s+(\d{4})$/i);
  if (m2) {
    const dd = String(m2[1]).padStart(2, "0");
    const monName = String(m2[2] || "").toLowerCase();
    const yyyy = String(m2[3]);
    const mon = MONTHS_NL[monName];
    if (mon) {
      const mm = String(mon).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  }

  return "";
}

function speechToStatus(raw) {
  const x = norm(raw);
  if (!x) return "";

  if (
    x.includes("klaar") ||
    x.includes("gesloten") ||
    x.includes("afgerond") ||
    x.includes("gereed") ||
    x === "af" ||
    x.includes("done")
  ) return "done";

  if (x.includes("open") || x.includes("openstaand") || x.includes("actief")) return "open";

  return "";
}

function upsertById(arr, item) {
  const id = item?.id;
  if (id === null || id === undefined) return arr;
  const idx = arr.findIndex((x) => String(x.id) === String(id));
  if (idx === -1) return [item, ...arr];
  const copy = arr.slice();
  copy[idx] = { ...copy[idx], ...item };
  return copy;
}

function removeById(arr, id) {
  return arr.filter((x) => String(x.id) !== String(id));
}

// ---------------- component ----------------
const emptyForm = {
  id: null,
  type: "note",
  organisation_id: "",
  contact_id: "",
  personnel_id: "",
  company_code_id: "",
  status: "open",
  due_date: "",
  due_time: "00:00",
  assigned_user_id: "",
  created_by_user_id: "",
  reminder_hours: 24,
  title: "",
  body: "",
};

export default function NotitiesTaken() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";
  let currentUser = null;
  let currentUserId = "";

  try {
    const parsed = JSON.parse(rawUser || "{}");
    role = parsed?.role || "user";
    currentUser = parsed || null;
    currentUserId = parsed?.id || "";
  } catch {}

  const isReadonly = String(role || "").toLowerCase() === "readonly";
  const VERSION = "vNT-009";

  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [listIndex, setListIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");

  const [reloadKey, setReloadKey] = useState(0);
  const [speechInfo, setSpeechInfo] = useState("");

const [q, setQ] = useState(getParam("q"));
const [type, setType] = useState(getParam("type"));
const [titleFilter, setTitleFilter] = useState("");
const [status, setStatus] = useState(getParam("status"));
const [dueFilter, setDueFilter] = useState("");
const [orgFilterId, setOrgFilterId] = useState(getParam("organisation_id") || getParam("organisatie_id"));
const [contactFilterId, setContactFilterId] = useState(getParam("contact_id") || getParam("contactpersoon_id"));
const [personnelFilterId, setPersonnelFilterId] = useState(getParam("personnel_id") || getParam("personeel_id"));
const [phoneFilter, setPhoneFilter] = useState("");
const [mobileFilter, setMobileFilter] = useState("");
const [emailFilter, setEmailFilter] = useState("");
const [personnelPhoneFilter, setPersonnelPhoneFilter] = useState("");
const [personnelMobileFilter, setPersonnelMobileFilter] = useState("");
const [personnelEmailFilter, setPersonnelEmailFilter] = useState("");
const [userFilter, setUserFilter] = useState("");
const [createdByFilter, setCreatedByFilter] = useState("");
const [companyCodeFilter, setCompanyCodeFilter] = useState("");
const [mode, setMode] = useState("search"); // search | list | form
const isFormLocked = mode !== "form";
const [sort, setSort] = useState({ key: "default", dir: "asc" });

const EMPTY_VAL = "__EMPTY__";

  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const midScrollRef = useRef(null);
  const rightScrollRef = useRef(null);

const formRefs = {
    type: useRef(null),
    org: useRef(null),
    contact: useRef(null),
    personnel: useRef(null),
    status: useRef(null),
    user: useRef(null),
    creator: useRef(null),
    due: useRef(null),
    title: useRef(null),
    body: useRef(null),
  };

  const selectedRelation = orgs.find(
    (o) => String(o.id) === String(form.organisation_id || "")
  );

  const selectedPersonnel = personnel.find(
    (p) => String(p.id) === String(form.personnel_id || "")
  );

  const selectedRelationCompanyCodeId =
    selectedRelation?.company_code_id !== null && selectedRelation?.company_code_id !== undefined
      ? String(selectedRelation.company_code_id)
      : "";

  const selectedPersonnelCompanyCodeId =
    selectedPersonnel?.company_code_id !== null && selectedPersonnel?.company_code_id !== undefined
      ? String(selectedPersonnel.company_code_id)
      : "";

  const companyCodeOptionsForForm = useMemo(() => {
    const allowed = selectedCompanyCodesForForm(companyCodes);

    const byId = new Map();
    allowed.forEach((cc) => {
      if (cc?.id) byId.set(String(cc.id), cc);
    });

    if (selectedRelationCompanyCodeId && !byId.has(selectedRelationCompanyCodeId)) {
      byId.set(selectedRelationCompanyCodeId, {
        id: selectedRelationCompanyCodeId,
        name: selectedRelation?.company_code_name || selectedRelation?.company_code || "Bedrijfscode relatie",
        active: true,
      });
    }

    if (selectedPersonnelCompanyCodeId && !byId.has(selectedPersonnelCompanyCodeId)) {
      byId.set(selectedPersonnelCompanyCodeId, {
        id: selectedPersonnelCompanyCodeId,
        name: selectedPersonnel?.company_code_name || selectedPersonnel?.company_code || "Bedrijfscode personeel",
        active: true,
      });
    }

    if (selectedRelationCompanyCodeId && !selectedPersonnelCompanyCodeId) {
      return Array.from(byId.values()).filter((cc) => String(cc.id) === selectedRelationCompanyCodeId);
    }

    if (!selectedRelationCompanyCodeId && selectedPersonnelCompanyCodeId) {
      return Array.from(byId.values()).filter((cc) => String(cc.id) === selectedPersonnelCompanyCodeId);
    }

    if (selectedRelationCompanyCodeId && selectedPersonnelCompanyCodeId) {
      const ids = new Set([selectedRelationCompanyCodeId, selectedPersonnelCompanyCodeId]);
      return Array.from(byId.values()).filter((cc) => ids.has(String(cc.id)));
    }

    return Array.from(byId.values());
  }, [
    companyCodes,
    selectedRelationCompanyCodeId,
    selectedPersonnelCompanyCodeId,
    selectedRelation?.company_code_name,
    selectedRelation?.company_code,
    selectedPersonnel?.company_code_name,
    selectedPersonnel?.company_code,
  ]);

  const selectedRelationIsPrivate = isPrivateRelation(selectedRelation);

  const formOrder = selectedRelationIsPrivate
    ? ["type", "org", "personnel", "status", "user", "creator", "due", "title", "body"]
    : ["type", "org", "contact", "personnel", "status", "user", "creator", "due", "title", "body"];

  useEffect(() => {
    if (!selectedRelationIsPrivate) return;
    if (!form.contact_id) return;

    setForm((prev) => ({
      ...prev,
      contact_id: "",
    }));
  }, [selectedRelationIsPrivate, form.contact_id]);

  useEffect(() => {
    setForm((prev) => {
      let nextCompanyCodeId = prev.company_code_id || "";

      if (selectedRelationCompanyCodeId && !selectedPersonnelCompanyCodeId) {
        nextCompanyCodeId = selectedRelationCompanyCodeId;
      } else if (!selectedRelationCompanyCodeId && selectedPersonnelCompanyCodeId) {
        nextCompanyCodeId = selectedPersonnelCompanyCodeId;
      } else if (selectedRelationCompanyCodeId && selectedPersonnelCompanyCodeId) {
        const validPair = new Set([selectedRelationCompanyCodeId, selectedPersonnelCompanyCodeId]);
        if (!prev.company_code_id || !validPair.has(String(prev.company_code_id))) {
          nextCompanyCodeId = selectedRelationCompanyCodeId;
        }
      } else if (!selectedRelationCompanyCodeId && !selectedPersonnelCompanyCodeId && !prev.company_code_id) {
        nextCompanyCodeId = firstCompanyCodeIdFromScope(companyCodes);
      }

      const allowedIds = new Set((companyCodeOptionsForForm || []).map((cc) => String(cc.id)));
      if (allowedIds.size && nextCompanyCodeId && !allowedIds.has(String(nextCompanyCodeId))) {
        nextCompanyCodeId = (companyCodeOptionsForForm[0]?.id && String(companyCodeOptionsForForm[0].id)) || "";
      }

      if (String(prev.company_code_id || "") === String(nextCompanyCodeId || "")) return prev;

      return {
        ...prev,
        company_code_id: nextCompanyCodeId || "",
      };
    });
  }, [
    selectedRelationCompanyCodeId,
    selectedPersonnelCompanyCodeId,
    companyCodes,
    companyCodeOptionsForForm,
  ]);


  const [focusIndex, setFocusIndex] = useState(0);

  // ✅ NEW: "grote tekst editor" modus + door-dicteren modus
  const [bodyEditor, setBodyEditor] = useState(false);
  const bodyDictateActiveRef = useRef(false);
  const bodyDictateRunsRef = useRef(0);

  function focusByIndex(idx) {
    const key = formOrder[Math.max(0, Math.min(formOrder.length - 1, idx))];
    const r = formRefs[key]?.current;
    if (r && typeof r.focus === "function") r.focus();
  }
  function focusNext() {
    setMode("form");
    setFocusIndex((i) => {
      const n = Math.min(formOrder.length - 1, i + 1);
      setTimeout(() => focusByIndex(n), 0);
      return n;
    });
  }
  function focusPrev() {
    setMode("form");
    setFocusIndex((i) => {
      const n = Math.max(0, i - 1);
      setTimeout(() => focusByIndex(n), 0);
      return n;
    });
  }

  function scrollToIndex(idx) {
    const el = rowRefs.current[idx];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }

  function highlightIndex(idx, arrLen) {
    const n = arrLen || 0;
    if (!n) {
      setListIndex(0);
      return;
    }
    const i = Math.max(0, Math.min(n - 1, idx));
    setListIndex(i);
    setMode("list");
    setTimeout(() => scrollToIndex(i), 0);
  }

  function formHasId() {
    return form.id !== null && form.id !== undefined && String(form.id).length > 0;
  }

function loadItemFromRow(it) {
  setSelectedId(it.id);

  setForm({
    id: it.id,
    type: it.type || it.kind || it.soort || "note",
    organisation_id:
      it.organisation_id !== null && it.organisation_id !== undefined
        ? String(it.organisation_id)
        : String(it.organisatie_id || ""),
    contact_id:
      it.contact_id !== null && it.contact_id !== undefined
        ? String(it.contact_id)
        : String(it.contactpersoon_id || ""),
    personnel_id:
      it.personnel_id !== null && it.personnel_id !== undefined
        ? String(it.personnel_id)
        : String(it.personeel_id || ""),
    company_code_id:
      it.company_code_id !== null && it.company_code_id !== undefined
        ? String(it.company_code_id)
        : "",
    status: it.status || "open",
    assigned_user_id:
      it.assigned_user_id !== null && it.assigned_user_id !== undefined
        ? String(it.assigned_user_id)
        : "",
    created_by_user_id:
      it.created_by_user_id !== null && it.created_by_user_id !== undefined
        ? String(it.created_by_user_id)
        : "",
    due_date: (it.due_date || it.vervaldatum || "").toString().slice(0, 10),
    due_time: it.due_time || "00:00",
    reminder_hours: it.reminder_hours ?? 24,
    title: it.title || it.titel || it.subject || "",
    body: it.body || it.tekst || it.text || "",
  });

  setMode("form");
  setFocusIndex(selectedRelationIsPrivate ? 4 : 5);
  setSaveError(null);
  setFlash("");
}

function jumpToRightColumn() {
    setMode("form");

    if (window.innerWidth <= 768) {
      focusFirstRightFieldMobile();
    } else {
      setTimeout(() => {
        setFocusIndex(0);
        focusByIndex(0);
      }, 0);
    }
  }

  function focusFirstRightFieldMobile() {
    setTimeout(() => {
      try {
        const panel = rightScrollRef.current;
        if (!panel) return;

        panel.scrollTop = 0;
        panel.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

// ---------- LOAD ORGS + CONTACTS + USERS ----------
  useEffect(() => {
    let cancelled = false;

    async function loadMeta() {
      try {
        const orgData = await tryFetchJson([
          "/api/organisations?q=",
          "/api/organisaties?q=",
          "/api/organisations",
          "/api/organisaties",
        ]);
        if (!cancelled) setOrgs(Array.isArray(orgData) ? orgData : []);
      } catch {
        if (!cancelled) setOrgs([]);
      }

      try {
        const contactData = await tryFetchJson([
          "/api/contacts?q=",
          "/api/contactpersonen?q=",
          "/api/contacts",
          "/api/contactpersonen",
        ]);
        if (!cancelled) setContacts(Array.isArray(contactData) ? contactData : []);
      } catch {
        if (!cancelled) setContacts([]);
      }

      try {
        const personnelData = await tryFetchJson([
          "/api/personnel?q=",
          "/api/personeel?q=",
          "/api/personnel",
          "/api/personeel",
        ]);
        if (!cancelled) setPersonnel(Array.isArray(personnelData) ? personnelData : []);
      } catch {
        if (!cancelled) setPersonnel([]);
      }

      try {
        const scopeData = await tryFetchJson([
          "/api/company-scope",
        ]);
        if (!cancelled) setCompanyCodes(Array.isArray(scopeData?.company_codes) ? scopeData.company_codes : []);
      } catch {
        if (!cancelled) setCompanyCodes([]);
      }

      try {
        const userData = await tryFetchJson([
          "/api/users",
        ]);
        if (!cancelled) setUsers(Array.isArray(userData) ? userData : []);
      } catch {
        if (!cancelled) setUsers([]);
      }
    }

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- LOAD ITEMS ----------
  const serverQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (type) p.set("type", type);
    if (status) p.set("status", status);
    if (orgFilterId) {
      p.set("organisation_id", orgFilterId);
      p.set("organisatie_id", orgFilterId);
    }
    if (contactFilterId) {
      p.set("contact_id", contactFilterId);
      p.set("contactpersoon_id", contactFilterId);
    }
    if (personnelFilterId) {
      p.set("personnel_id", personnelFilterId);
      p.set("personeel_id", personnelFilterId);
    }
    return p.toString();
  }, [type, status, orgFilterId, contactFilterId, personnelFilterId]);

  useEffect(() => {
    let cancelled = false;

    async function loadItems() {
      setLoading(true);
      setError(null);
      try {
const data = await tryFetchJson([
  serverQuery ? `/api/notes-tasks?${serverQuery}` : `/api/notes-tasks`,
]);
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [serverQuery, reloadKey]);

  // ---------- CLIENT FILTER ----------

const titleColOptions = useMemo(() => {
  const vals = Array.from(
    new Set(items.map((it) => String(pickItemTitle(it) || "").trim()).filter(Boolean))
  );
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const dueColOptions = useMemo(() => {
  const vals = Array.from(
    new Set(items.map((it) => String(formatDateNL(pickItemDueISO(it)) || "").trim()).filter(Boolean))
  );
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const titleHasEmpty = useMemo(
  () => items.some((it) => !String(pickItemTitle(it) || "").trim()),
  [items]
);

const dueHasEmpty = useMemo(
  () => items.some((it) => !String(formatDateNL(pickItemDueISO(it)) || "").trim()),
  [items]
);

const filteredItems = useMemo(() => {
  const qq = norm(q);
  const arr = items.slice();

  const byQ = (it) => {
    if (!qq) return true;
    const hay = norm([
      pickItemTitle(it),
      pickItemOrgName(it),
      pickItemContactName(it),
      pickItemPhone(it),
      pickItemMobile(it),
      pickItemEmail(it),
      pickItemPersonnelName(it),
      pickItemPersonnelPhone(it),
      pickItemPersonnelMobile(it),
      pickItemPersonnelEmail(it),
      pickCompanyCodeName(it),
    ].join(" "));
    return hay.includes(qq);
  };

const byType = (it) =>
  !type ? true : String(it.type || it.soort || "").toLowerCase() === String(type).toLowerCase();

const byTitle = (it) => {
  if (!titleFilter) return true;
  const v = String(pickItemTitle(it) || "").trim();
  if (titleFilter === EMPTY_VAL) return !v;
  return v === titleFilter;
};

const byStatus = (it) =>
  !status ? true : String(it.status || "").toLowerCase() === String(status).toLowerCase();

const byDue = (it) => {
  if (!dueFilter) return true;
  const v = String(formatDateNL(pickItemDueISO(it)) || "").trim();
  if (dueFilter === EMPTY_VAL) return !v;
  return v === dueFilter;
};

const byOrg = (it) =>
  !orgFilterId ? true : String(it.organisation_id || it.organisatie_id || "") === String(orgFilterId);

  const byContact = (it) =>
    !contactFilterId ? true : String(it.contact_id || it.contactpersoon_id || "") === String(contactFilterId);

  const byPersonnel = (it) =>
    !personnelFilterId ? true : String(it.personnel_id || it.personeel_id || "") === String(personnelFilterId);

  const byPhone = (it) => {
    if (!phoneFilter) return true;
    const v = String(pickItemPhone(it) || "").trim();
    if (phoneFilter === EMPTY_VAL) return !v;
    return v === phoneFilter;
  };

  const byMobile = (it) => {
    if (!mobileFilter) return true;
    const v = String(pickItemMobile(it) || "").trim();
    if (mobileFilter === EMPTY_VAL) return !v;
    return v === mobileFilter;
  };

const byEmail = (it) => {
    if (!emailFilter) return true;
    const v = String(pickItemEmail(it) || "").trim();
    if (emailFilter === EMPTY_VAL) return !v;
    return v === emailFilter;
  };

  const byPersonnelPhone = (it) => {
    if (!personnelPhoneFilter) return true;
    const v = String(pickItemPersonnelPhone(it) || "").trim();
    if (personnelPhoneFilter === EMPTY_VAL) return !v;
    return v === personnelPhoneFilter;
  };

  const byPersonnelMobile = (it) => {
    if (!personnelMobileFilter) return true;
    const v = String(pickItemPersonnelMobile(it) || "").trim();
    if (personnelMobileFilter === EMPTY_VAL) return !v;
    return v === personnelMobileFilter;
  };

  const byPersonnelEmail = (it) => {
    if (!personnelEmailFilter) return true;
    const v = String(pickItemPersonnelEmail(it) || "").trim();
    if (personnelEmailFilter === EMPTY_VAL) return !v;
    return v === personnelEmailFilter;
  };

  const byUser = (it) => {
    if (!userFilter) return true;
    const v = String(it.assigned_user_id || "").trim();
    if (userFilter === EMPTY_VAL) return !v;
    return v === userFilter;
  };

  const byCreatedBy = (it) => {
    if (!createdByFilter) return true;
    const v = String(it.created_by_user_id || "").trim();
    if (createdByFilter === EMPTY_VAL) return !v;
    return v === createdByFilter;
  };

  const byCompanyCode = (it) => {
    if (!companyCodeFilter) return true;
    const v = String(pickCompanyCodeName(it) || "").trim();
    if (companyCodeFilter === EMPTY_VAL) return !v;
    return v === companyCodeFilter;
  };

return arr.filter(
  (it) =>
    byQ(it) &&
    byType(it) &&
    byTitle(it) &&
    byStatus(it) &&
    byDue(it) &&
    byOrg(it) &&
    byContact(it) &&
    byPersonnel(it) &&
    byPhone(it) &&
    byMobile(it) &&
    byEmail(it) &&
    byPersonnelPhone(it) &&
    byPersonnelMobile(it) &&
    byPersonnelEmail(it) &&
    byUser(it) &&
    byCreatedBy(it) &&
    byCompanyCode(it)
);
}, [items, q, type, titleFilter, status, dueFilter, orgFilterId, contactFilterId, personnelFilterId, phoneFilter, mobileFilter, emailFilter, personnelPhoneFilter, personnelMobileFilter, personnelEmailFilter, userFilter, createdByFilter, companyCodeFilter]);

  // ---------- SORT ----------
const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();

    const statusRank = (s) => (String(s || "").toLowerCase() === "done" ? 1 : 0);
    const dueKey = (it) => {
      const iso = pickItemDueISO(it);
      return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : "";
    };

const defaultSort = (a, b) => {
      const aMine = String(a.assigned_user_id || "") === String(currentUserId || "");
      const bMine = String(b.assigned_user_id || "") === String(currentUserId || "");

      if (aMine !== bMine) return aMine ? -1 : 1;

      const aS = statusRank(pickItemStatus(a));
      const bS = statusRank(pickItemStatus(b));
      if (aS !== bS) return aS - bS;

      const ad = dueKey(a);
      const bd = dueKey(b);

      const aNo = ad === "";
      const bNo = bd === "";
      if (aNo !== bNo) return aNo ? 1 : -1;

      if (ad !== bd) return ad.localeCompare(bd);

      return Number(a.id || 0) - Number(b.id || 0);
    };

    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmpText = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const cmpISO = (a, b) => {
      const aa = String(a || "");
      const bb = String(b || "");
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

const customSort = (a, b) => {
      if (sort.key === "default") return defaultSort(a, b);

      if (sort.key === "type") return dirMul * cmpText(a.type || a.soort || "", b.type || b.soort || "");
      if (sort.key === "title") return dirMul * cmpText(pickItemTitle(a), pickItemTitle(b));
      if (sort.key === "status") return dirMul * cmpText(pickItemStatus(a), pickItemStatus(b));
      if (sort.key === "due") return dirMul * cmpISO(dueKey(a) || "9999-99-99", dueKey(b) || "9999-99-99");
      if (sort.key === "org") return dirMul * cmpText(pickItemOrgName(a), pickItemOrgName(b));
      if (sort.key === "contact") return dirMul * cmpText(pickItemContactName(a), pickItemContactName(b));
      if (sort.key === "personnel") return dirMul * cmpText(pickItemPersonnelName(a), pickItemPersonnelName(b));
      if (sort.key === "phone") return dirMul * cmpText(pickItemPhone(a), pickItemPhone(b));
      if (sort.key === "mobile") return dirMul * cmpText(pickItemMobile(a), pickItemMobile(b));
if (sort.key === "email") return dirMul * cmpText(pickItemEmail(a), pickItemEmail(b));
      if (sort.key === "personnel_phone") return dirMul * cmpText(pickItemPersonnelPhone(a), pickItemPersonnelPhone(b));
      if (sort.key === "personnel_mobile") return dirMul * cmpText(pickItemPersonnelMobile(a), pickItemPersonnelMobile(b));
      if (sort.key === "personnel_email") return dirMul * cmpText(pickItemPersonnelEmail(a), pickItemPersonnelEmail(b));
      if (sort.key === "user") return dirMul * cmpText(a.assigned_user_name || "", b.assigned_user_name || "");
      if (sort.key === "created_by") return dirMul * cmpText(a.created_by_user_name || "", b.created_by_user_name || "");
      if (sort.key === "company_code") return dirMul * cmpText(pickCompanyCodeName(a), pickCompanyCodeName(b));

      return defaultSort(a, b);
    };

    copy.sort(customSort);
    return copy;
  }, [filteredItems, sort]);

  const gridCount = Math.min(sortedItems.length, 9999);

  useEffect(() => {
    const n = sortedItems.length;
    if (!n) {
      setListIndex(0);
      return;
    }
    if (listIndex < 0 || listIndex >= n) highlightIndex(0, n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedItems.length]);

  // ---------- ACTIES ----------
  function scrollTopAll() {
    if (midScrollRef.current) midScrollRef.current.scrollTop = 0;
    if (rightScrollRef.current) rightScrollRef.current.scrollTop = 0;
  }

function vernieuwList() {
  setQ("");
  setType("");
  setTitleFilter("");
  setStatus("");
  setDueFilter("");
  setOrgFilterId("");
  setContactFilterId("");
  setPersonnelFilterId("");
  setPhoneFilter("");
  setMobileFilter("");
setEmailFilter("");
  setPersonnelPhoneFilter("");
  setPersonnelMobileFilter("");
  setPersonnelEmailFilter("");
  setUserFilter("");
  setCreatedByFilter("");
  setCompanyCodeFilter("");

  // rechterkolom wél leegmaken
  setSelectedId(null);
  setForm({ ...emptyForm, company_code_id: firstCompanyCodeIdFromScope(companyCodes) });
  setListIndex(0);

  setSaveError(null);
  setFlash("Vernieuwd ✅");
  setSort({ key: "default", dir: "asc" });

  setReloadKey((k) => k + 1);
  setTimeout(() => setFlash(""), 800);

  // altijd terug naar zoeken
  setMode("search");
  if (window.innerWidth > 768) {
    setTimeout(() => {
      try {
        searchRef.current?.focus?.();
      } catch {}
    }, 0);
  }

  setTimeout(scrollTopAll, 0);

  // stop eventueel door-dicteren
  bodyDictateActiveRef.current = false;
  bodyDictateRunsRef.current = 0;
  setBodyEditor(false);
}

function newItem() {
    setSelectedId(null);
    setForm({
      ...emptyForm,
      type: type || "note",
      status: status || "open",
      organisation_id: orgFilterId || "",
      contact_id: contactFilterId || "",
      personnel_id: personnelFilterId || "",
      company_code_id: firstCompanyCodeIdFromScope(companyCodes),
      assigned_user_id: currentUserId || "",
      created_by_user_id: currentUserId || "",
      due_date: new Date().toISOString().slice(0, 10),
      due_time: "00:00",
      reminder_hours: 24,
    });
    setSaveError(null);
    setFlash("");
    setMode("form");

    if (window.innerWidth <= 768) {
      focusFirstRightFieldMobile();
    } else {
      setTimeout(() => {
        setFocusIndex(0);
        focusByIndex(0);
        if (rightScrollRef.current) rightScrollRef.current.scrollTop = 0;
      }, 0);
    }

    bodyDictateActiveRef.current = false;
    bodyDictateRunsRef.current = 0;
    setBodyEditor(false);
  }

async function saveItem() {
    if (isReadonly) {
      alert("Alleen-lezen modus. Opslaan niet toegestaan.");
      return;
    }

    setSaveError(null);
    setFlash("");

    const payload = {
      id: form.id,
      kind: form.type || "note",
      organisation_id: form.organisation_id || null,
      contact_id: form.contact_id || null,
      personnel_id: form.personnel_id || null,
      company_code_id: form.company_code_id || null,
      status: form.status || "open",

      due_date: form.due_date || null,
      due_time: form.due_time || "00:00",

      assigned_user_id: form.assigned_user_id || null,
      created_by_user_id: form.created_by_user_id || currentUserId || form.assigned_user_id || null,
      reminder_hours: Number(form.reminder_hours || 0),

      subject: form.title || "",
      body: form.body || "",
      priority: null,
      labels: null,
      active: true,
    };

    const isUpdate = formHasId();
    const method = isUpdate ? "PUT" : "POST";

    const urls = isUpdate
      ? [`/api/notes-tasks/${form.id}`]
      : ["/api/notes-tasks"];

    try {
      const saved = await tryFetchJson(urls, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!saved || typeof saved !== "object") {
        setReloadKey((k) => k + 1);
        setSelectedId(null);
        setForm(emptyForm);
        setListIndex(0);

        setFlash("Opgeslagen ✅");
        setTimeout(() => setFlash(""), 800);

        setMode("search");
        setTimeout(() => searchRef.current?.focus?.(), 0);
        return;
      }

      const savedRow = {
        ...saved,
        type: saved.type || saved.kind || form.type || "note",
        title: saved.title ?? saved.subject ?? form.title ?? "",
        due_date: (saved.due_date || form.due_date || "").toString().slice(0, 10),
        due_time: saved.due_time || form.due_time || "00:00",
        assigned_user_id: saved.assigned_user_id || form.assigned_user_id || "",
        assigned_user_name:
          saved.assigned_user_name ||
          users.find((u) => String(u.id) === String(saved.assigned_user_id || form.assigned_user_id))?.display_name ||
          users.find((u) => String(u.id) === String(saved.assigned_user_id || form.assigned_user_id))?.email ||
          "",
        created_by_user_id: saved.created_by_user_id || form.created_by_user_id || "",
        created_by_user_name:
          saved.created_by_user_name ||
          users.find((u) => String(u.id) === String(saved.created_by_user_id || form.created_by_user_id))?.display_name ||
          users.find((u) => String(u.id) === String(saved.created_by_user_id || form.created_by_user_id))?.email ||
          "",
        reminder_hours: saved.reminder_hours ?? form.reminder_hours ?? 24,

        organisation_id: saved.organisation_id ?? form.organisation_id ?? null,
        contact_id: saved.contact_id ?? form.contact_id ?? null,
        personnel_id: saved.personnel_id ?? form.personnel_id ?? null,

        organisation_name:
          saved.organisation_name ||
          pickOrgName(
            orgs.find(
              (o) =>
                String(o.id) ===
                String(saved.organisation_id ?? form.organisation_id)
            )
          ),

        contact_name:
          saved.contact_name ||
          pickContactName(
            contacts.find(
              (c) =>
                String(c.id) ===
                String(saved.contact_id ?? form.contact_id)
            )
          ),

        personnel_name:
          saved.personnel_name ||
          pickPersonnelName(
            personnel.find(
              (p) =>
                String(p.id) ===
                String(saved.personnel_id ?? form.personnel_id)
            )
          ),
        personnel_phone:
          saved.personnel_phone ||
          personnel.find((p) => String(p.id) === String(saved.personnel_id ?? form.personnel_id))?.phone ||
          "",
        personnel_mobile:
          saved.personnel_mobile ||
          personnel.find((p) => String(p.id) === String(saved.personnel_id ?? form.personnel_id))?.mobile ||
          "",
        personnel_email:
          saved.personnel_email ||
          personnel.find((p) => String(p.id) === String(saved.personnel_id ?? form.personnel_id))?.email ||
          "",
        company_code_id: saved.company_code_id ?? form.company_code_id ?? null,
        company_code_name:
          saved.company_code_name ||
          findCompanyCodeName(companyCodes, saved.company_code_id ?? form.company_code_id) ||
          "",
        company_code: saved.company_code || "",
      };

setItems((prev) => upsertById(prev, savedRow));

      // Na opslaan altijd opnieuw laden uit database.
      // Dit voorkomt lege gebruiker/contactpersoon bij nieuwe invoer.
      setReloadKey((k) => k + 1);

      setSelectedId(null);
      setForm(emptyForm);
      setListIndex(0);

      setFlash("Opgeslagen ✅");
      setTimeout(() => setFlash(""), 800);

      setMode("search");
      setTimeout(() => searchRef.current?.focus?.(), 0);
      return;
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonly) {
    alert("Alleen-lezen modus. Verwijderen niet toegestaan.");
    return;
  }
    setSaveError(null);
    setFlash("");
    if (!formHasId()) return;

    const ok = window.confirm("Weet je zeker dat je dit item wilt verwijderen?");
    if (!ok) return;

    const id = form.id;
const urls = [`/api/notes-tasks/${id}`];

    try {
      await tryFetchJson(urls, { method: "DELETE" });

      setItems((prev) => removeById(prev, id));

      setSelectedId(null);
      setForm(emptyForm);
      setListIndex(0);

      setFlash("Verwijderd ✅");
      setTimeout(() => setFlash(""), 800);

      setMode("search");
      setTimeout(() => searchRef.current?.focus?.(), 0);
      return;
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  // ---------- refs tegen stale state ----------
  const listIndexRef = useRef(0);
  const modeRef = useRef("search");
  const focusIndexRef = useRef(0);
  const lenRef = useRef(0);

  useEffect(() => { listIndexRef.current = listIndex; }, [listIndex]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { focusIndexRef.current = focusIndex; }, [focusIndex]);
  useEffect(() => { lenRef.current = sortedItems.length; }, [sortedItems.length]);

  // anti-dubbel-commando lock
  const cmdLockRef = useRef({ key: "", ts: 0 });
  function commandLocked(key, ms = 450) {
    const now = Date.now();
    if (cmdLockRef.current.key === key && now - cmdLockRef.current.ts < ms) return true;
    cmdLockRef.current = { key, ts: now };
    return false;
  }

  // ---------- SPEECH ----------
  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 4500, // blijft jouw stabiele PTT
    onCommand: (cmd, rawHeard) => {
      if (!cmd || !cmd.type) return false;

      setSpeechInfo(`🎙 ${rawHeard}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      // als we bezig zijn met door-dicteren in Tekst, dan mag "stop/klaar" dat beëindigen
      if (bodyDictateActiveRef.current && (cmd.type === "clear" || cmd.type === "refresh" || cmd.type === "save" || cmd.type === "delete")) {
        bodyDictateActiveRef.current = false;
        bodyDictateRunsRef.current = 0;
      }

      if (cmd.type === "next") {
        if (commandLocked("next")) return true;
        const m = modeRef.current;
        if (m === "form") { focusNext(); return true; }
        const n = lenRef.current;
        if (!n) return true;
        highlightIndex(Math.min(n - 1, listIndexRef.current + 1), n);
        return true;
      }

      if (cmd.type === "prev") {
        if (commandLocked("prev")) return true;
        const m = modeRef.current;
        if (m === "form") { focusPrev(); return true; }
        const n = lenRef.current;
        if (!n) return true;
        highlightIndex(Math.max(0, listIndexRef.current - 1), n);
        return true;
      }

      if (cmd.type === "new") {
        if (commandLocked("new", 700)) return true;
        newItem();
        return true;
      }

      if (cmd.type === "refresh") {
        if (commandLocked("refresh", 700)) return true;
        vernieuwList();
        return true;
      }

      if (cmd.type === "save") {
        if (commandLocked("save", 700)) return true;
        saveItem();
        return true;
      }

      if (cmd.type === "delete") {
        if (commandLocked("delete", 700)) return true;
        deleteItem();
        return true;
      }

if (cmd.type === "select") {
        if (commandLocked("select", 500)) return true;
        const row = sortedItems[listIndexRef.current];
        if (row) {
          loadItemFromRow(row);
          setMode("form");

          if (window.innerWidth <= 768) {
            focusFirstRightFieldMobile();
          } else {
            setTimeout(() => {
              setFocusIndex(0);
              focusByIndex(0);
            }, 0);
          }
        }
        return true;
      }

      if (cmd.type === "list") {
        setMode("list");
        return true;
      }

      if (cmd.type === "clear") {
        const m = modeRef.current;
        if (m === "search") { setQ(""); return true; }
        if (m === "form") {
          const key = formOrder[focusIndexRef.current];
          setForm((f) => {
            const nf = { ...f };
            if (key === "title") nf.title = "";
            if (key === "body") nf.body = "";
            if (key === "due") nf.due_date = "";
            if (key === "status") nf.status = "open";
            return nf;
          });
          return true;
        }
        return true;
      }

      if (cmd.type === "punt") {
        if (modeRef.current === "form") {
          const key = formOrder[focusIndexRef.current];
          if (key === "title") setForm((f) => ({ ...f, title: replacePuntToken((f.title || "") + " .") }));
          if (key === "body") setForm((f) => ({ ...f, body: replacePuntToken((f.body || "") + " .") }));
        }
        return true;
      }

      return false;
    },

    onDictation: (text) => {
      const raw0 = String(text || "").trim();
      if (!raw0) return;

      setSpeechInfo(`🎙 ${raw0}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw0);
      const w1 = firstWord(t);

      // EXTRA: stop/klaar tijdens Tekst-dicteren
      if (bodyDictateActiveRef.current) {
        if (t === "stop" || t === "klaar" || t === "klaar met tekst" || t === "einde") {
          bodyDictateActiveRef.current = false;
          bodyDictateRunsRef.current = 0;
          return;
        }
      }

      // snelle dictation-commando’s
      if (w1 === "vernieuw" || w1 === "vernieuwen") {
        if (!commandLocked("refresh", 700)) vernieuwList();
        return;
      }
      if (w1 === "opslaan" || w1 === "bewaren") {
        if (!commandLocked("save", 700)) saveItem();
        return;
      }
      if (w1 === "verwijder" || w1 === "verwijderen" || w1 === "delete") {
        if (!commandLocked("delete", 700)) deleteItem();
        return;
      }

if (w1 === "selecteer" || t === "item" || t === "rechts") {
        if (!commandLocked("select", 500)) {
          const row = sortedItems[listIndexRef.current];
          if (row) {
            loadItemFromRow(row);
            setMode("form");

            if (window.innerWidth <= 768) {
              focusFirstRightFieldMobile();
            } else {
              setTimeout(() => {
                setFocusIndex(0);
                focusByIndex(0);
              }, 0);
            }
          }
        }
        return;
      }

      if (t === "lijst" || t === "links") {
        setMode("list");
        return;
      }

      if (t === "leeg") {
        if (modeRef.current === "search") setQ("");
        if (modeRef.current === "form") {
          const key = formOrder[focusIndexRef.current];
          setForm((f) => {
            const nf = { ...f };
            if (key === "title") nf.title = "";
            if (key === "body") nf.body = "";
            if (key === "due") nf.due_date = "";
            if (key === "status") nf.status = "open";
            return nf;
          });
        }
        return;
      }

      // SEARCH
      if (modeRef.current === "search") {
        if (isEmptyWord(raw0)) { setQ(""); return; }
        setQ(raw0);
        setTimeout(() => highlightIndex(0, sortedItems.length), 0);
        return;
      }

      // FORM
      if (modeRef.current === "form") {
        const key = formOrder[focusIndexRef.current];
        const rawVal = isEmptyWord(raw0) ? "" : raw0;

        if (key === "type") {
          const x = norm(rawVal);
          setForm((f) => ({ ...f, type: x.includes("taak") ? "task" : "note" }));
          focusNext();
          return;
        }

        if (key === "org") {
          if (!rawVal) { setForm((f) => ({ ...f, organisation_id: "" })); focusNext(); return; }
          const hit =
            orgs.find((o) => norm(pickOrgName(o)).startsWith(norm(rawVal))) ||
            orgs.find((o) => norm(pickOrgName(o)).includes(norm(rawVal)));
          setForm((f) => ({ ...f, organisation_id: hit ? String(hit.id) : "" }));
          focusNext();
          return;
        }

        if (key === "contact") {
          if (!rawVal) { setForm((f) => ({ ...f, contact_id: "" })); focusNext(); return; }
          const hit =
            contacts.find((c) => norm(pickContactName(c)).startsWith(norm(rawVal))) ||
            contacts.find((c) => norm(pickContactName(c)).includes(norm(rawVal)));
          setForm((f) => ({ ...f, contact_id: hit ? String(hit.id) : "" }));
          focusNext();
          return;
        }

        if (key === "status") {
          const st = speechToStatus(rawVal);
          if (st) { setForm((f) => ({ ...f, status: st })); focusNext(); }
          return;
        }

        if (key === "due") {
          if (!rawVal) { setForm((f) => ({ ...f, due_date: "" })); focusNext(); return; }
          const iso = parseDateFromSpeechToISO(rawVal);
          if (iso) { setForm((f) => ({ ...f, due_date: iso })); focusNext(); }
          return;
        }

        if (key === "title") {
          const v = capFirst(replacePuntToken(rawVal));
          setForm((f) => ({ ...f, title: v }));
          focusNext();
          return;
        }

        if (key === "body") {
          // ✅ BODY: append i.p.v. vervangen (zodat meerdere zinnen achter elkaar kunnen)
          const v = capFirst(replacePuntToken(rawVal));
          setForm((f) => {
            const prev = String(f.body || "").trim();
            const next = prev ? `${prev}\n${v}` : v;
            return { ...f, body: next };
          });

          // ✅ als we in door-dicteren modus zitten: start automatisch nog een keer
          if (bodyDictateActiveRef.current) {
            bodyDictateRunsRef.current += 1;
            if (bodyDictateRunsRef.current <= 25) {
              setTimeout(() => {
                try {
                  if (bodyDictateActiveRef.current) voice.startOnce();
                } catch {}
              }, 250);
            } else {
              bodyDictateActiveRef.current = false;
              bodyDictateRunsRef.current = 0;
            }
          }
          return;
        }
      }
    },
  });

// ---------- LAYOUT ----------
  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;

  const TABLE_MIN_WIDTH = 1690;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

const colWidths = {
  type: 48,
  title: 86,
  status: 48,
  due: 72,
  org: 95,
  contact: 95,
  phone: 75,
  mobile: 75,
  email: 90,
  personnel: 95,
  personnelPhone: 75,
  personnelMobile: 75,
  personnelEmail: 90,
  user: 80,
  createdBy: 90,
  companyCode: 90,
};

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const selectStyle = {
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    boxSizing: "border-box",
  };

  const tableStyle = { width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

const rowBgCursor = "#f3f7ff";
const rowBgSelected = "#e6f0ff";

const phoneColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemPhone(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const mobileColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemMobile(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const emailColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemEmail(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const phoneHasEmpty = useMemo(() => items.some((it) => !String(pickItemPhone(it) || "").trim()), [items]);
const mobileHasEmpty = useMemo(() => items.some((it) => !String(pickItemMobile(it) || "").trim()), [items]);
const emailHasEmpty = useMemo(() => items.some((it) => !String(pickItemEmail(it) || "").trim()), [items]);

const personnelPhoneColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemPersonnelPhone(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const personnelMobileColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemPersonnelMobile(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const personnelEmailColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemPersonnelEmail(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const personnelPhoneHasEmpty = useMemo(() => items.some((it) => !String(pickItemPersonnelPhone(it) || "").trim()), [items]);
const personnelMobileHasEmpty = useMemo(() => items.some((it) => !String(pickItemPersonnelMobile(it) || "").trim()), [items]);
const personnelEmailHasEmpty = useMemo(() => items.some((it) => !String(pickItemPersonnelEmail(it) || "").trim()), [items]);

const companyCodeColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickCompanyCodeName(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const companyCodeHasEmpty = useMemo(() => items.some((it) => !String(pickCompanyCodeName(it) || "").trim()), [items]);


  const commandHelp =
    "Commando’s: volgende | vorige/terug | nieuw | vernieuw | opslaan | verwijderen | item/selecteer/rechts | lijst/links | leeg | punt | status open/klaar | (Tekst: stop/klaar)";

  function sortToggle(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  const deleteEnabled = formHasId();

  // ✅ helper: start speak, maar als focus op body + bodyEditor aan => door-dicteren activeren
  function startSpeakSmart() {
    if (!voice.supported || voice.listening) return;

    const inBody = modeRef.current === "form" && formOrder[focusIndexRef.current] === "body";
    if (inBody && bodyEditor) {
      bodyDictateActiveRef.current = true;
      bodyDictateRunsRef.current = 0;
    } else {
      bodyDictateActiveRef.current = false;
      bodyDictateRunsRef.current = 0;
    }

    voice.startOnce();
  }

  return (
    <div>
<div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        {/* LINKS */}
        <div style={{ minWidth: 0 }}>

<h1 style={{ marginTop: 0, marginBottom: 10 }}>
            Notities/Taken <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>

{/* Kopregel */}
          <>
            {!isMobile ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "nowrap",
                }}
              >
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setMode("search");
                    setBodyEditor(false);
                  }}
                  placeholder="Zoek…"
                  style={searchInputStyle}
                  onFocus={() => {
                    setMode("search");
                    setBodyEditor(false);
                  }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    minWidth: 52,
                    height: 32,
                    padding: "0 10px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                  }}
                >
                  {gridCount}
                </div>

                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                  onClick={startSpeakSmart}
                  disabled={!voice.supported || voice.listening}
                  style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                  title={
                    bodyEditor
                      ? "In Tekst: 1x klikken = door-dicteren (stop/klaar om te stoppen)"
                      : "1x klikken = 1 commando dicteren"
                  }
                >
                  {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
                </button>

<button
  onClick={newItem}
  disabled={isReadonly}
  style={isReadonly ? buttonDisabledStyle : buttonStyle}
>
  ➕ Nieuw
</button>

                <button onClick={vernieuwList} style={buttonStyle}>
                  🔄 Vernieuw
                </button>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) 58px",
                    gap: 6,
                    marginBottom: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    ref={searchRef}
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setMode("search");
                      setBodyEditor(false);
                    }}
                    placeholder="Zoek…"
                    style={{
                      ...searchInputStyle,
                      width: "100%",
                      minWidth: 0,
                    }}
                    onFocus={() => {
                      setMode("search");
                      setBodyEditor(false);
                    }}
                  />

                  <div
                    title={`Aantal regels in het grid: ${gridCount}`}
                    style={{
                      height: 34,
                      minHeight: 34,
                      padding: "0 8px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 4,
                      background: "#f7fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#1f4e79",
                      boxSizing: "border-box",
                      whiteSpace: "nowrap",
                      lineHeight: "1",
                    }}
                  >
                    {gridCount}
                  </div>
                </div>

<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 6,
  }}
>
  <button
    onMouseDown={(e) => e.preventDefault()}
    onTouchStart={(e) => e.preventDefault()}
    onClick={startSpeakSmart}
    disabled={!voice.supported || voice.listening}
    style={{
      ...(!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle),
      padding: "10px 8px",
      fontSize: 13,
    }}
    title={
      bodyEditor
        ? "In Tekst: 1x klikken = door-dicteren (stop/klaar om te stoppen)"
        : "1x klikken = 1 commando dicteren"
    }
  >
    {voice.listening ? "🎤..." : "🎤 Spreek"}
  </button>

  <button
    onClick={newItem}
    disabled={isReadonly}
    style={{
      ...(isReadonly
        ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
        : buttonStyle),
      padding: "10px 8px",
      fontSize: 13,
    }}
  >
    ➕ Nieuw
  </button>

  <button
    onClick={vernieuwList}
    style={{
      ...buttonStyle,
      padding: "10px 8px",
      fontSize: 13,
    }}
  >
    🔄 Vernieuw
  </button>
</div>

              </>
            )}
          </>

          <div style={{ marginTop: 10 }}>
            {loading && <div>Laden…</div>}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
            {saveError && <div style={{ marginBottom: 8, color: "crimson" }}>{saveError}</div>}
{voice.error && voice.error !== "no-speech" && (
  <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>
)}
          </div>

          {!loading && !error && (
            <div style={{ marginTop: 8, minWidth: 0 }}>
              <div
                ref={midScrollRef}
                style={{
                  overflowX: "scroll",
                  overflowY: "auto",
                  maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  background: "#fff",
                }}
              >
                <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                  <table style={tableStyle}>
<colgroup>
  <col style={{ width: colWidths.type }} />
  <col style={{ width: colWidths.title }} />
  <col style={{ width: colWidths.status }} />
  <col style={{ width: colWidths.due }} />
  <col style={{ width: colWidths.org }} />
  <col style={{ width: colWidths.contact }} />
  <col style={{ width: colWidths.phone }} />
  <col style={{ width: colWidths.mobile }} />
  <col style={{ width: colWidths.email }} />
  <col style={{ width: colWidths.personnel }} />
  <col style={{ width: colWidths.personnelPhone }} />
  <col style={{ width: colWidths.personnelMobile }} />
  <col style={{ width: colWidths.personnelEmail }} />
  <col style={{ width: colWidths.user }} />
  <col style={{ width: colWidths.createdBy }} />
  <col style={{ width: colWidths.companyCode }} />
</colgroup>
                    <thead>
<tr>
  <th style={thClickable} onClick={() => sortToggle("type")}>Type{sortArrow("type")}</th>
  <th style={thClickable} onClick={() => sortToggle("title")}>Titel{sortArrow("title")}</th>
  <th style={thClickable} onClick={() => sortToggle("status")}>Status{sortArrow("status")}</th>
  <th style={thClickable} onClick={() => sortToggle("due")}>Vervaldatum{sortArrow("due")}</th>
  <th style={thClickable} onClick={() => sortToggle("org")}>Relatie{sortArrow("org")}</th>
  <th style={thClickable} onClick={() => sortToggle("contact")}>Contactpersoon{sortArrow("contact")}</th>
  <th style={thClickable} onClick={() => sortToggle("phone")}>Telefoon{sortArrow("phone")}</th>
  <th style={thClickable} onClick={() => sortToggle("mobile")}>Mobiel{sortArrow("mobile")}</th>
<th style={thClickable} onClick={() => sortToggle("email")}>E-mail{sortArrow("email")}</th>
  <th style={thClickable} onClick={() => sortToggle("personnel")}>Personeel{sortArrow("personnel")}</th>
  <th style={thClickable} onClick={() => sortToggle("personnel_phone")}>Telefoon{sortArrow("personnel_phone")}</th>
  <th style={thClickable} onClick={() => sortToggle("personnel_mobile")}>Mobiel{sortArrow("personnel_mobile")}</th>
  <th style={thClickable} onClick={() => sortToggle("personnel_email")}>E-mail{sortArrow("personnel_email")}</th>
  <th style={thClickable} onClick={() => sortToggle("user")}>Uitvoerder{sortArrow("user")}</th>
  <th style={thClickable} onClick={() => sortToggle("created_by")}>Aangemaakt door{sortArrow("created_by")}</th>
  <th style={thClickable} onClick={() => sortToggle("company_code")}>Bedrijfscode{sortArrow("company_code")}</th>
</tr>

<tr>
  <th style={filterThStyle}>
    <select value={type} onChange={(e) => setType(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      <option value="note">Notitie</option>
      <option value="task">Taak</option>
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={titleFilter} onChange={(e) => setTitleFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {titleHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {titleColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      <option value="open">Open</option>
      <option value="done">Klaar</option>
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={dueFilter} onChange={(e) => setDueFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {dueHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {dueColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

<th style={filterThStyle}>
    <select value={orgFilterId || ""} onChange={(e) => setOrgFilterId(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {orgs.map((o) => (
        <option key={o.id} value={String(o.id)}>{pickOrgSearchName(o)}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={contactFilterId || ""} onChange={(e) => setContactFilterId(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {contacts.map((c) => (
        <option key={c.id} value={String(c.id)}>{pickContactName(c)}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {phoneHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {phoneColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={mobileFilter} onChange={(e) => setMobileFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {mobileHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {mobileColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

<th style={filterThStyle}>
    <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {emailHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {emailColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={personnelFilterId || ""} onChange={(e) => setPersonnelFilterId(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {personnel.map((p) => (
        <option key={p.id} value={String(p.id)}>{pickPersonnelName(p)}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={personnelPhoneFilter} onChange={(e) => setPersonnelPhoneFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {personnelPhoneHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {personnelPhoneColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={personnelMobileFilter} onChange={(e) => setPersonnelMobileFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {personnelMobileHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {personnelMobileColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={personnelEmailFilter} onChange={(e) => setPersonnelEmailFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {personnelEmailHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {personnelEmailColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      <option value={EMPTY_VAL}>(leeg)</option>
      {users.map((u) => (
        <option key={u.id} value={String(u.id)}>
          {u.display_name || u.name || u.email}
        </option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={createdByFilter} onChange={(e) => setCreatedByFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      <option value={EMPTY_VAL}>(leeg)</option>
      {users.map((u) => (
        <option key={u.id} value={String(u.id)}>
          {u.display_name || u.name || u.email}
        </option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={companyCodeFilter} onChange={(e) => setCompanyCodeFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {companyCodeHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {companyCodeColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>
</tr>
                    </thead>

                    <tbody>
                      {sortedItems.map((it, idx) => {
                        const isCursor = idx === listIndex;
                        const isSelected = selectedId === it.id;

                        let bg = "transparent";
                        if (isSelected) bg = rowBgSelected;
                        else if (isCursor) bg = rowBgCursor;

                        const dueISO = pickItemDueISO(it);
                        const dueNL = dueISO ? formatDateNL(dueISO) : "";

                        const s = String(pickItemStatus(it) || "").toLowerCase();
                        const statusLabel = s === "done" ? "klaar" : s || "-";

                        return (
                          <tr
                            key={it.id}
                            ref={(el) => (rowRefs.current[idx] = el)}
                            onClick={() => {
                              highlightIndex(idx, sortedItems.length);
                              loadItemFromRow(it);
                              jumpToRightColumn();
                              setBodyEditor(false);
                            }}
                            style={{ cursor: "pointer", backgroundColor: bg }}
                          >

<td style={tdStyle}>{(it.type || it.soort) === "task" ? "Taak" : "Notitie"}</td>
<td style={tdStyle} title={pickItemTitle(it) || ""}>{pickItemTitle(it) || "-"}</td>
<td style={tdStyle}>{statusLabel}</td>
<td style={tdStyle}>{dueNL || "-"}</td>
<td style={tdStyle}>
  {pickOrgSearchName(
    orgs.find((o) => String(o.id) === String(it.organisation_id || it.organisatie_id || ""))
  ) || pickItemOrgName(it) || "-"}
</td>
<td style={tdStyle}>{pickItemContactName(it) || "-"}</td>

<td style={tdStyle} title={pickItemPhone(it) || ""}>
  {pickItemPhone(it) ? (
    <button
      type="button"
      onClick={(e) => handlePhoneGridClick(e, pickItemPhone(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen: ${pickItemPhone(it)}`}
    >
      {pickItemPhone(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={pickItemMobile(it) || ""}>
  {pickItemMobile(it) ? (
    <button
      type="button"
      onClick={(e) => handleMobileGridClick(e, pickItemMobile(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen / SMS / WhatsApp: ${pickItemMobile(it)}`}
    >
      {pickItemMobile(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={pickItemEmail(it) || ""}>
  {pickItemEmail(it) ? (
    <button
      type="button"
      onClick={(e) => handleEmailGridClick(e, pickItemEmail(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`E-mailen: ${pickItemEmail(it)}`}
    >
      {pickItemEmail(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle}>{pickItemPersonnelName(it) || "-"}</td>

<td style={tdStyle} title={pickItemPersonnelPhone(it) || ""}>
  {pickItemPersonnelPhone(it) ? (
    <button
      type="button"
      onClick={(e) => handlePhoneGridClick(e, pickItemPersonnelPhone(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen: ${pickItemPersonnelPhone(it)}`}
    >
      {pickItemPersonnelPhone(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={pickItemPersonnelMobile(it) || ""}>
  {pickItemPersonnelMobile(it) ? (
    <button
      type="button"
      onClick={(e) => handleMobileGridClick(e, pickItemPersonnelMobile(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen / SMS / WhatsApp: ${pickItemPersonnelMobile(it)}`}
    >
      {pickItemPersonnelMobile(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={pickItemPersonnelEmail(it) || ""}>
  {pickItemPersonnelEmail(it) ? (
    <button
      type="button"
      onClick={(e) => handleEmailGridClick(e, pickItemPersonnelEmail(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`E-mailen: ${pickItemPersonnelEmail(it)}`}
    >
      {pickItemPersonnelEmail(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle}>{it.assigned_user_name || "-"}</td>
<td style={tdStyle}>{pickItemCreatedByName(it) || "-"}</td>
<td style={tdStyle} title={pickCompanyCodeName(it)}>{pickCompanyCodeName(it) || "-"}</td>

                          </tr>
                        );
                      })}

                      {sortedItems.length === 0 && (
                        <tr>
                          <td colSpan={16} style={{ padding: 12, opacity: 0.7 }}>
                            Geen resultaten (filters te streng?)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RECHTS */}
<div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : 320,
            minWidth: isMobile ? 0 : 320,
            maxWidth: isMobile ? "100%" : 320,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div style={{ background: "white", borderRadius: 10, padding: 12, boxSizing: "border-box" }}>
            <h3 style={{ marginTop: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Item</span>
              {bodyEditor && (
                <button
                  onClick={() => {
                    setBodyEditor(false);
                    bodyDictateActiveRef.current = false;
                    bodyDictateRunsRef.current = 0;
                  }}
                  style={buttonStyle}
                  title="Terug naar normaal formulier"
                >
                  ↩ Terug
                </button>
              )}
            </h3>

            {/* ✅ Als bodyEditor aan staat: toon alleen grote tekst-editor + knoppen */}
            {bodyEditor ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Tekst-editor (spreek: door-dicteren) — zeg <b>stop</b> of <b>klaar</b> om te stoppen.
                </div>

                <textarea
                  ref={formRefs.body}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={15}
                  style={{
                    padding: "10px 10px",
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    background: "white",
                    resize: "vertical",
                    boxSizing: "border-box",
                    minHeight: "60vh",
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                  onFocus={() => {
                    setMode("form");
                    setFocusIndex(selectedRelationIsPrivate ? 7 : 8);
                  }}
                  onBlur={() => {
                    bodyDictateActiveRef.current = false;
                    bodyDictateRunsRef.current = 0;
                  }}
                />

<div style={{ display: "flex", gap: 10 }}>
  <button
    onClick={saveItem}
    disabled={isFormLocked || isReadonly}
    style={
      isFormLocked || isReadonly
        ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
        : buttonPrimary("#2f7dd1")
    }
  >
    💾 Opslaan
  </button>

  <button
    onClick={deleteItem}
    disabled={isFormLocked || !deleteEnabled || isReadonly}
    style={
      isFormLocked || !deleteEnabled || isReadonly
        ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
        : buttonPrimary("#d64545")
    }
  >
    🗑 Verwijderen
  </button>
</div>

              </div>
            ) : (
              // normaal formulier (jouw v09)
              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 13, color: "#333" }}>Type</label>
<select
  ref={formRefs.type}
  value={form.type}
  onChange={(e) => setForm({ ...form, type: e.target.value })}
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
  onFocus={() => { setMode("form"); setFocusIndex(0); }}
>
                  <option value="note">Notitie</option>
                  <option value="task">Taak</option>
                </select>

                <label style={{ fontSize: 13, color: "#333" }}>Relatie</label>
<select
  ref={formRefs.org}
  value={form.organisation_id}
  onChange={(e) =>
    setForm({
      ...form,
      organisation_id: e.target.value,
      contact_id: "",
    })
  }
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
  onFocus={() => { setMode("form"); setFocusIndex(1); }}
>
                  <option value="">(geen)</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={String(o.id)}>{pickOrgSearchName(o)}</option>
                  ))}
                </select>

<label
                  style={{
                    fontSize: 13,
                    color: "#333",
                    opacity: selectedRelationIsPrivate ? 0.55 : 1,
                  }}
                >
                  Contactpersoon
                </label>
<select
  ref={formRefs.contact}
  value={selectedRelationIsPrivate ? "" : form.contact_id}
  onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
  disabled={isFormLocked || isReadonly || selectedRelationIsPrivate}
  style={{
    ...selectStyle,
    opacity: selectedRelationIsPrivate ? 0.55 : 1,
    cursor: selectedRelationIsPrivate ? "not-allowed" : "pointer",
  }}
  onFocus={() => {
    if (selectedRelationIsPrivate) return;
    setMode("form");
    setFocusIndex(2);
  }}
>
                  <option value="">
                    {selectedRelationIsPrivate
                      ? "(niet van toepassing bij particulier)"
                      : "(geen)"}
                  </option>
{!selectedRelationIsPrivate &&
  contacts
    .filter((c) => {
      // alleen actieve contactpersonen
      if (c.active === false) return false;

      // als er een relatie gekozen is -> alleen die relatie
      if (form.organisation_id && String(c.organisation_id) !== String(form.organisation_id)) {
        return false;
      }

      return true;
    })
    .map((c) => (
      <option key={c.id} value={c.id}>
        {pickContactName(c)}
      </option>
    ))}
                </select>

                <label style={{ fontSize: 13, color: "#333" }}>Personeel</label>
<select
  ref={formRefs.personnel}
  value={form.personnel_id || ""}
  onChange={(e) => setForm({ ...form, personnel_id: e.target.value })}
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
  onFocus={() => { setMode("form"); setFocusIndex(selectedRelationIsPrivate ? 2 : 3); }}
>
                  <option value="">(geen)</option>
                  {personnel.map((p) => (
                    <option key={p.id} value={String(p.id)}>{pickPersonnelName(p)}</option>
                  ))}
                </select>

                <label style={{ fontSize: 13, color: "#333" }}>Bedrijfscode</label>
<select
  value={form.company_code_id || ""}
  onChange={(e) => setForm({ ...form, company_code_id: e.target.value })}
  disabled={isFormLocked || isReadonly || companyCodeOptionsForForm.length <= 1}
  style={{
    ...selectStyle,
    opacity: companyCodeOptionsForForm.length <= 1 ? 0.85 : 1,
  }}
>
  {companyCodeOptionsForForm.length === 0 && <option value="">(geen)</option>}
  {companyCodeOptionsForForm.map((cc) => (
    <option key={cc.id} value={String(cc.id)}>
      {cc.name || cc.code}
    </option>
  ))}
</select>

                <label style={{ fontSize: 13, color: "#333" }}>Status</label>
<select
  ref={formRefs.status}
  value={form.status}
  onChange={(e) => setForm({ ...form, status: e.target.value })}
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
  onFocus={() => { setMode("form"); setFocusIndex(selectedRelationIsPrivate ? 3 : 4); }}
>
                  <option value="open">Open</option>
                  <option value="done">Klaar</option>
                </select>

<label style={{ fontSize: 13, color: "#333" }}>Uitvoerder</label>
<select
  ref={formRefs.user}
  value={form.assigned_user_id || ""}
  onChange={(e) =>
    setForm({
      ...form,
      assigned_user_id: e.target.value || "",
    })
  }
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
  onFocus={() => {
    setMode("form");
    setFocusIndex(Math.max(0, formOrder.indexOf("user")));
  }}
>
  <option value="">(geen)</option>
  {users.map((u) => (
    <option key={u.id} value={String(u.id)}>
      {u.display_name || u.name || u.email}
    </option>
  ))}
</select>

<label style={{ fontSize: 13, color: "#333" }}>Aangemaakt door</label>
<select
  ref={formRefs.creator}
  value={form.created_by_user_id || ""}
  onChange={(e) =>
    setForm({
      ...form,
      created_by_user_id: e.target.value || "",
    })
  }
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
  onFocus={() => {
    setMode("form");
    setFocusIndex(Math.max(0, formOrder.indexOf("creator")));
  }}
>
  <option value="">(geen)</option>
  {users.map((u) => (
    <option key={u.id} value={String(u.id)}>
      {u.display_name || u.name || u.email}
    </option>
  ))}
</select>

<label style={{ fontSize: 13, color: "#333" }}>Vervaldatum</label>
<input
  ref={formRefs.due}
  type="date"
  value={form.due_date || ""}
  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
  disabled={isFormLocked || isReadonly}
  style={{
    padding: "7px 10px",
    width: "100%",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  }}
/>

<label style={{ fontSize: 13, color: "#333" }}>Tijd</label>
<input
  type="time"
  value={form.due_time || "00:00"}
  onChange={(e) => setForm({ ...form, due_time: e.target.value })}
  disabled={isFormLocked || isReadonly}
  style={{
    padding: "7px 10px",
    width: "100%",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  }}
/>

<label style={{ fontSize: 13, color: "#333" }}>Reminder e-mail</label>
<select
  value={String(form.reminder_hours ?? 24)}
  onChange={(e) =>
    setForm({ ...form, reminder_hours: Number(e.target.value || 0) })
  }
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
>
  <option value="0">Geen reminder</option>
  <option value="2">2 uur vooraf</option>
  <option value="24">24 uur vooraf</option>
  <option value="48">48 uur vooraf</option>
  <option value="168">7 dagen vooraf</option>
</select>

                <label style={{ fontSize: 13, color: "#333" }}>Titel</label>
<input
  ref={formRefs.title}
  value={form.title}
  onChange={(e) => setForm({ ...form, title: e.target.value })}
  disabled={isFormLocked || isReadonly}
  style={{
    padding: "7px 10px",
    width: "100%",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  }}
  onFocus={() => { setMode("form"); setFocusIndex(Math.max(0, formOrder.indexOf("title"))); }}
/>

                <label style={{ fontSize: 13, color: "#333" }}>Tekst</label>
<textarea
  ref={formRefs.body}
  value={form.body}
  onChange={(e) => setForm({ ...form, body: e.target.value })}
  disabled={isFormLocked || isReadonly}
  rows={8}
  style={{
    padding: "7px 10px",
    width: "100%",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    resize: "vertical",
    boxSizing: "border-box",
  }}
  onFocus={() => {
    if (isFormLocked || isReadonly) return;

    setMode("form");
    setFocusIndex(Math.max(0, formOrder.indexOf("body")));

    // ✅ automatisch naar grote editor zodra je met Tekst bezig bent
    setBodyEditor(true);

    // zet focus direct in grote editor
    setTimeout(() => formRefs.body.current?.focus?.(), 0);
  }}
/>

<div style={{ display: "flex", gap: 10, marginTop: 6 }}>
  <button
    onClick={saveItem}
    disabled={isFormLocked || isReadonly}
    style={
      isFormLocked || isReadonly
        ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
        : buttonPrimary("#2f7dd1")
    }
  >
    💾 Opslaan
  </button>

  <button
    onClick={deleteItem}
    disabled={isFormLocked || !deleteEnabled || isReadonly}
    style={
      isFormLocked || !deleteEnabled || isReadonly
        ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
        : buttonPrimary("#d64545")
    }
  >
    🗑 Verwijderen
  </button>
</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}